import { useEffect, useState } from 'react';

import {
    Button,
    ButtonKind,
    Footer,
    Status,
    StatusType,
} from '../../shared/ModalV2';
import Selector from '../../shared/Selector';
import { Loading } from '../../shared';

import { useAuthenticate, useEnvironments } from '../../hooks/environments';
import { usePacksList } from '../../hooks/packs';
import {
    useCreatePacksDrawer,
    usePacksDrawerList,
    usePacksDrawerRecord,
    useUpdatePacksDrawerInEnv,
} from '../../hooks/packsDrawer';

import { copyMedias } from '../../utils/upload';

import { MediaType } from '../../types/enums/mediatype.enum';

import { Environment } from '../../types/environmentTypes';

import styles from './CopyPacksDrawer.module.scss';

type CopyPacksDrawerProps = {
    recordId: number;
    onClose: () => void;
};

function CopyPacksDrawer(props: CopyPacksDrawerProps) {
    const { recordId, onClose } = props;
    const [targetEnvName, setTargetEnvName] = useState<string>('');
    const [isCopying, setIsCopying] = useState<boolean>(false);
    const [copyError, setCopyError] = useState<string | undefined>();
    const [copied, setCopied] = useState<boolean>(false);

    const {
        environments = [],
        isLoading: isEnvsLoading,
        error: envsError,
    } = useEnvironments();
    const {
        isAuthenticated,
        isLoading: isAuthLoading,
        error: authError,
    } = useAuthenticate(targetEnvName);
    const currentEnv = environments.find((env) => env.active);
    const otherEnvs = environments.filter((env) => !env.active);
    const targetEnv = environments.find((env) => env.name === targetEnvName);
    const targetEnvBasePath = targetEnv?.basepath;

    const {
        record,
        isLoading: isRecordLoading,
        errorMessage: recordError,
    } = usePacksDrawerRecord(String(recordId));
    const {
        records: targetRecords,
        isLoading: isTargetRecordsLoading,
        fetch: refetchTargetRecords,
        errorMessage: targetRecordsError,
    } = usePacksDrawerList({ basepath: targetEnvBasePath });
    const { packs: targetPacks } = usePacksList({
        basepath: targetEnvBasePath,
        enabled: Boolean(targetEnvName && isAuthenticated),
    });
    const { createRecord } = useCreatePacksDrawer({
        basepath: targetEnvBasePath,
    });
    const { updateRecord } = useUpdatePacksDrawerInEnv({
        basepath: targetEnvBasePath,
    });

    useEffect(() => {
        if (targetEnvName && isAuthenticated) {
            refetchTargetRecords();
        }
    }, [targetEnvName, isAuthenticated]);

    async function copyThumbnail(target: Environment): Promise<number | null> {
        if (!record?.thumbnail) return null;

        let failed = false;
        const [newMedia] = await copyMedias(
            [
                {
                    url: record.thumbnail.url,
                    // The thumbnail type is always the pack_drawer_thumb media type
                    type: record.thumbnail.type as MediaType,
                },
            ],
            record.packId,
            target,
            (_res, success) => {
                if (!success) failed = true;
            },
        );
        if (failed) {
            throw new Error(
                `Failed to copy thumbnail for pack ${record.packId}`,
            );
        }
        return newMedia?.mediaId ?? null;
    }

    async function copyAll(target: Environment) {
        if (!record) return;

        setCopyError(undefined);
        setIsCopying(true);

        try {
            const packExists = (targetPacks ?? []).some(
                (pack) => pack.packId === record.packId,
            );
            if (!packExists) {
                throw new Error(
                    `Pack ${record.packId} does not exist in ${target.name}. Copy the pack to ${target.name} first, then copy the packs drawer entry.`,
                );
            }

            const thumbnailMediaId = await copyThumbnail(target);

            const body = {
                active: record.active,
                isFree: record.isFree,
                thumbnailMediaId,
            };
            const targetRecord = targetRecords.find(
                (r) => r.packId === record.packId,
            );

            if (targetRecord) {
                await updateRecord(targetRecord.id, body);
            } else {
                await createRecord({
                    packId: record.packId,
                    ...body,
                });
            }

            setCopied(true);
        } catch (err) {
            setCopyError(
                err instanceof Error
                    ? err.message
                    : 'Failed to copy packs drawer record to target environment',
            );
        } finally {
            setIsCopying(false);
        }
    }

    if (
        isEnvsLoading ||
        isAuthLoading ||
        isRecordLoading ||
        (targetEnvName && isTargetRecordsLoading) ||
        isCopying
    ) {
        return (
            <>
                <Loading />
                <Footer>
                    <Button kind={ButtonKind.SECONDARY} onClick={onClose}>
                        Close
                    </Button>
                </Footer>
            </>
        );
    }

    if (
        envsError ??
        (targetEnvName && authError) ??
        recordError ??
        (targetEnvName && targetRecordsError)
    ) {
        return (
            <>
                <Status
                    type={StatusType.Error}
                    header="Oops, something went wrong"
                    body={
                        envsError ??
                        authError ??
                        recordError ??
                        targetRecordsError ??
                        'Please try again or contact system administrator.'
                    }
                />
                <Footer>
                    <Button kind={ButtonKind.SECONDARY} onClick={onClose}>
                        Close
                    </Button>
                </Footer>
            </>
        );
    }

    if (copied) {
        return (
            <>
                <Status
                    type={StatusType.Info}
                    header={`Packs drawer entry for ${record?.packId} successfully copied to ${targetEnvName}`}
                    body="You can now close the pop up. Please verify the entry in the selected environment."
                />
                <Footer>
                    <Button kind={ButtonKind.SECONDARY} onClick={onClose}>
                        Close
                    </Button>
                </Footer>
            </>
        );
    }

    if (!record) return null;

    const targetRecord = targetRecords.find((r) => r.packId === record.packId);
    const canCopy = Boolean(targetEnv);
    const sourceThumbnailUrl = record.thumbnail?.url.replace('{0}', '@2x');
    const targetThumbnailUrl = targetRecord?.thumbnail?.url.replace(
        '{0}',
        '@2x',
    );

    return (
        <section>
            <header className={styles.header}>
                <h2 className={styles.headerTitle}>
                    Preview & Copy Packs Drawer Entry to Environment
                </h2>
                <div>
                    Copy to:
                    <Selector
                        value={targetEnvName}
                        onChange={(e) => setTargetEnvName(e.target.value)}
                    >
                        <option disabled value="">
                            Select environment
                        </option>
                        {otherEnvs.map((env) => (
                            <option key={env.name} value={env.name}>
                                {env.name}
                            </option>
                        ))}
                    </Selector>
                </div>
            </header>
            {copyError && <div className={styles.error}>{copyError}</div>}
            <table className={styles.previewTable}>
                <thead>
                    <tr>
                        <th />
                        <th>
                            From{' '}
                            <b className={styles.envName}>{currentEnv?.name}</b>
                        </th>
                        <th>
                            {targetEnvName ? (
                                <>
                                    To{' '}
                                    <b className={styles.envName}>
                                        {targetEnvName}
                                    </b>{' '}
                                    {targetRecord
                                        ? '(will be updated)'
                                        : '(will be created)'}
                                </>
                            ) : (
                                'Select target environment'
                            )}
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td>Pack ID</td>
                        <td>{record.packId}</td>
                        <td>{targetRecord?.packId ?? record.packId}</td>
                    </tr>
                    <tr>
                        <td>Active</td>
                        <td>{record.active ? 'Yes' : 'No'}</td>
                        <td>
                            {targetRecord
                                ? targetRecord.active
                                    ? 'Yes'
                                    : 'No'
                                : record.active
                                  ? 'Yes'
                                  : 'No'}
                        </td>
                    </tr>
                    <tr>
                        <td>Free</td>
                        <td>{record.isFree ? 'Yes' : 'No'}</td>
                        <td>
                            {targetRecord
                                ? targetRecord.isFree
                                    ? 'Yes'
                                    : 'No'
                                : record.isFree
                                  ? 'Yes'
                                  : 'No'}
                        </td>
                    </tr>
                    <tr>
                        <td>Thumbnail</td>
                        <td>
                            {sourceThumbnailUrl ? (
                                <img
                                    className={styles.thumbnail}
                                    src={sourceThumbnailUrl}
                                    alt="source thumbnail"
                                />
                            ) : (
                                '—'
                            )}
                        </td>
                        <td>
                            {targetThumbnailUrl ? (
                                <img
                                    className={styles.thumbnail}
                                    src={targetThumbnailUrl}
                                    alt="target thumbnail"
                                />
                            ) : (
                                '—'
                            )}
                        </td>
                    </tr>
                </tbody>
            </table>
            <Footer>
                <p className={styles.paragraph}>
                    Please note, this process can&apos;t be reversed. The pack
                    must already exist in the target environment.
                </p>
                <div>
                    <Button kind={ButtonKind.SECONDARY} onClick={onClose}>
                        Cancel
                    </Button>
                    <Button
                        kind={ButtonKind.PRIMARY}
                        disabled={!canCopy}
                        onClick={() =>
                            canCopy && targetEnv && copyAll(targetEnv)
                        }
                    >
                        Copy entry
                    </Button>
                </div>
            </Footer>
        </section>
    );
}

export default CopyPacksDrawer;
